import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { Cake, Users2, Download, CalendarIcon, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/StatusBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtBday = (dob) => {
  if (!dob || dob.length < 10) return "—";
  return `${MONTHS[parseInt(dob.slice(5, 7), 10) - 1]} ${parseInt(dob.slice(8, 10), 10)}`;
};

function downloadCSV(filename, rows, columns) {
  const header = columns.map((c) => c.label).join(",");
  const body = rows
    .map((r) => columns.map((c) => `"${String(c.get(r) ?? "").replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([`${header}\n${body}`], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const RANGES = [
  { key: "today", label: "Today" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "next_month", label: "Next Month" },
  { key: "custom", label: "Custom" },
];

function BirthdayReports() {
  const navigate = useNavigate();
  const [range, setRange] = useState("month");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [country, setCountry] = useState("");
  const [nationality, setNationality] = useState("");
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => {
    const params = { range };
    if (range === "custom") { if (start) params.start = start; if (end) params.end = end; }
    if (country) params.country = country;
    if (nationality) params.nationality = nationality;
    const r = await api.get("/birthdays", { params });
    setRows(r.data);
  }, [range, start, end, country, nationality]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => downloadCSV("birthdays.csv", rows, [
    { label: "Name", get: (r) => r.name }, { label: "Birthday", get: (r) => fmtBday(r.date_of_birth) },
    { label: "Company", get: (r) => r.company }, { label: "Country", get: (r) => r.country },
    { label: "Nationality", get: (r) => r.nationality }, { label: "Contact Type", get: (r) => r.contact_type },
    { label: "Consultant", get: (r) => r.consultant_name },
  ]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {RANGES.map((r) => (
          <button
            key={r.key}
            data-testid={`bday-range-${r.key}`}
            onClick={() => setRange(r.key)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${range === r.key ? "bg-primary text-white border-primary" : "border-border hover:bg-secondary"}`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {range === "custom" && (
        <div className="flex flex-wrap gap-3">
          {[["Start", start, setStart], ["End", end, setEnd]].map(([lbl, val, setter]) => (
            <div key={lbl} className="space-y-1">
              <Label className="text-xs text-muted-foreground">{lbl}</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button data-testid={`bday-${lbl.toLowerCase()}`} variant="outline" className="font-normal w-40 justify-start">
                    <CalendarIcon className="mr-2 h-4 w-4" />{val || "Pick"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={val ? new Date(val) : undefined} onSelect={(d) => setter(d ? format(d, "yyyy-MM-dd") : "")} /></PopoverContent>
              </Popover>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <div className="space-y-1"><Label className="text-xs text-muted-foreground">Country</Label><Input data-testid="bday-country" value={country} onChange={(e) => setCountry(e.target.value)} placeholder="e.g. Zimbabwe" className="w-48" /></div>
        <div className="space-y-1"><Label className="text-xs text-muted-foreground">Nationality</Label><Input data-testid="bday-nationality" value={nationality} onChange={(e) => setNationality(e.target.value)} placeholder="e.g. Indian" className="w-48" /></div>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{rows.length} result{rows.length !== 1 ? "s" : ""}</p>
        <Button data-testid="export-birthdays" variant="outline" size="sm" onClick={exportCsv} className="gap-1.5" disabled={!rows.length}><Download className="h-3.5 w-3.5" /> Export CSV</Button>
      </div>

      <div className="bg-white border border-border rounded-lg overflow-hidden">
        {rows.length ? (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="px-4 py-2.5 font-medium">Name</th><th className="px-3 py-2.5 font-medium">Birthday</th>
              <th className="px-3 py-2.5 font-medium">Company</th><th className="px-3 py-2.5 font-medium">Country</th>
              <th className="px-3 py-2.5 font-medium">Type</th><th className="px-3 py-2.5 font-medium">Consultant</th><th className="px-3 py-2.5"></th>
            </tr></thead>
            <tbody className="divide-y divide-border">
              {rows.map((b) => (
                <tr key={b.id} data-testid={`bday-report-row-${b.id}`} className="hover:bg-secondary/50">
                  <td className="px-4 py-3 font-semibold">{b.name || "—"}</td>
                  <td className="px-3 py-3"><span className="inline-flex items-center gap-1.5"><Cake className="h-3.5 w-3.5 text-pink-500" /> {fmtBday(b.date_of_birth)}</span></td>
                  <td className="px-3 py-3 text-muted-foreground">{b.company || "—"}</td>
                  <td className="px-3 py-3 text-muted-foreground">{b.country || "—"}</td>
                  <td className="px-3 py-3 text-muted-foreground">{b.contact_type || "Customer"}</td>
                  <td className="px-3 py-3 text-muted-foreground">{b.consultant_name || "—"}</td>
                  <td className="px-3 py-3 text-right"><button onClick={() => navigate(`/customers/${b.id}`)} className="text-muted-foreground hover:text-foreground"><ArrowUpRight className="h-4 w-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <div className="p-12 text-center text-sm text-muted-foreground">No birthdays for this filter.</div>}
      </div>
    </div>
  );
}

function CustomerReports() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [country, setCountry] = useState("");
  const [company, setCompany] = useState("");
  const [consultantId, setConsultantId] = useState("all");
  const [birthdays, setBirthdays] = useState("all");
  const [consultants, setConsultants] = useState([]);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (user?.role === "admin") api.get("/consultants").then((r) => setConsultants(r.data)).catch(() => {});
  }, [user]);

  const load = useCallback(async () => {
    const params = {};
    if (country) params.country = country;
    if (company) params.company = company;
    if (consultantId !== "all") params.consultant_id = consultantId;
    if (birthdays !== "all") params.birthdays = birthdays;
    const r = await api.get("/reports/customers", { params });
    setRows(r.data);
  }, [country, company, consultantId, birthdays]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => downloadCSV("customers.csv", rows, [
    { label: "Name", get: (r) => r.name }, { label: "Company", get: (r) => r.company },
    { label: "Country", get: (r) => r.country }, { label: "Phone", get: (r) => r.phone },
    { label: "Status", get: (r) => r.status }, { label: "DOB", get: (r) => r.date_of_birth },
    { label: "Consultant", get: (r) => r.consultant_name },
  ]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        <div className="space-y-1"><Label className="text-xs text-muted-foreground">Country</Label><Input data-testid="cr-country" value={country} onChange={(e) => setCountry(e.target.value)} placeholder="Country" className="w-40" /></div>
        <div className="space-y-1"><Label className="text-xs text-muted-foreground">Company</Label><Input data-testid="cr-company" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Company" className="w-40" /></div>
        {user?.role === "admin" && (
          <div className="space-y-1"><Label className="text-xs text-muted-foreground">Consultant</Label>
            <Select value={consultantId} onValueChange={setConsultantId}>
              <SelectTrigger data-testid="cr-consultant" className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All consultants</SelectItem>{consultants.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        <div className="space-y-1"><Label className="text-xs text-muted-foreground">Birthday</Label>
          <Select value={birthdays} onValueChange={setBirthdays}>
            <SelectTrigger data-testid="cr-birthday" className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="available">Birthday available</SelectItem>
              <SelectItem value="missing">Birthday missing</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{rows.length} customer{rows.length !== 1 ? "s" : ""}</p>
        <Button data-testid="export-customers" variant="outline" size="sm" onClick={exportCsv} className="gap-1.5" disabled={!rows.length}><Download className="h-3.5 w-3.5" /> Export CSV</Button>
      </div>

      <div className="bg-white border border-border rounded-lg overflow-hidden">
        {rows.length ? (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="px-4 py-2.5 font-medium">Name</th><th className="px-3 py-2.5 font-medium">Company</th>
              <th className="px-3 py-2.5 font-medium">Country</th><th className="px-3 py-2.5 font-medium">DOB</th>
              <th className="px-3 py-2.5 font-medium">Consultant</th><th className="px-3 py-2.5 font-medium">Status</th><th className="px-3 py-2.5"></th>
            </tr></thead>
            <tbody className="divide-y divide-border">
              {rows.map((c) => (
                <tr key={c.id} data-testid={`cr-row-${c.id}`} className="hover:bg-secondary/50">
                  <td className="px-4 py-3 font-semibold">{c.name || "—"}</td>
                  <td className="px-3 py-3 text-muted-foreground">{c.company || "—"}</td>
                  <td className="px-3 py-3 text-muted-foreground">{c.country || "—"}</td>
                  <td className="px-3 py-3 text-muted-foreground">{c.date_of_birth ? fmtBday(c.date_of_birth) : "—"}</td>
                  <td className="px-3 py-3 text-muted-foreground">{c.consultant_name || "—"}</td>
                  <td className="px-3 py-3"><StatusBadge status={c.status} /></td>
                  <td className="px-3 py-3 text-right"><button onClick={() => navigate(`/customers/${c.id}`)} className="text-muted-foreground hover:text-foreground"><ArrowUpRight className="h-4 w-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <div className="p-12 text-center text-sm text-muted-foreground">No customers for this filter.</div>}
      </div>
    </div>
  );
}

export default function Reports() {
  const [params] = useSearchParams();
  const initial = params.get("tab") === "customers" ? "customers" : "birthdays";
  return (
    <div className="p-8 max-w-[1400px] animate-in-up">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold tracking-tight">Reports</h1>
        <p className="text-muted-foreground mt-1">Birthday reminders and customer insights — no AI, just fast queries.</p>
      </div>
      <Tabs defaultValue={initial}>
        <TabsList className="mb-5">
          <TabsTrigger value="birthdays" data-testid="tab-birthdays" className="gap-1.5"><Cake className="h-4 w-4" /> Birthday Reports</TabsTrigger>
          <TabsTrigger value="customers" data-testid="tab-customers" className="gap-1.5"><Users2 className="h-4 w-4" /> Customer Reports</TabsTrigger>
        </TabsList>
        <TabsContent value="birthdays"><BirthdayReports /></TabsContent>
        <TabsContent value="customers"><CustomerReports /></TabsContent>
      </Tabs>
    </div>
  );
}
